import { api } from '../api/client';

function normalizeCheckoutPlan(planName) {
  const raw = String(planName || '').trim().toLowerCase();
  if (!raw) return null;
  if (['pro', 'professional'].includes(raw)) return 'pro';
  if (['team', 'team pro', 'teampro'].includes(raw)) return 'team';
  if (['basics', 'basic', 'solo'].includes(raw)) return 'basics';
  if (['personal'].includes(raw)) return 'personal';
  if (['family'].includes(raw)) return 'family';
  if (['enterprise', 'large company', 'large-company'].includes(raw)) return 'enterprise';
  return null;
}

function normalizeBillingPeriod(billingPeriod) {
  const raw = String(billingPeriod || '').trim().toLowerCase();
  if (['annual', 'yearly', 'year'].includes(raw)) return 'annual';
  if (['monthly', 'month'].includes(raw)) return 'monthly';
  return 'monthly';
}

/**
 * Redirect the authenticated user to the Stripe Customer Portal.
 */
export async function redirectToPortal({ fallbackPlanName = 'pro', fallbackBillingPeriod = 'monthly', fallbackQuantity } = {}) {
  try {
    const res = await api('/api/billing/create-portal-session', { method: 'POST' });
    if (!res || !res.ok) {
      const err = await res.json().catch(() => ({}));
      const message = err?.error || 'Failed to create portal session';
      // If no Stripe customer exists yet, route to checkout instead of hard-failing.
      if (res.status === 400 && /no billing account found/i.test(message)) {
        return redirectToCheckout({
          planName: fallbackPlanName,
          billingPeriod: fallbackBillingPeriod,
          quantity: fallbackQuantity,
        });
      }
      throw new Error(message);
    }
    const data = await res.json();
    window.location.href = data.url;
  } catch (err) {
    console.error('Stripe portal error:', err);
    alert('Could not open billing portal. Please try again.');
  }
}

/**
 * Redirect the authenticated user to Stripe Checkout.
 * @param {{ planName: string, billingPeriod: string, quantity?: number }} opts
 */
export async function redirectToCheckout({ planName, billingPeriod, quantity }) {
  const normalizedPlanName = normalizeCheckoutPlan(planName);
  const normalizedBillingPeriod = normalizeBillingPeriod(billingPeriod);

  if (!normalizedPlanName || normalizedPlanName === 'enterprise') {
    alert('Please select a valid paid plan to continue.');
    return;
  }

  try {
    const res = await api('/api/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({
        planName: normalizedPlanName,
        billingPeriod: normalizedBillingPeriod,
        quantity,
      }),
    });
    if (!res || !res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || 'Failed to create checkout session');
    }
    const data = await res.json();
    window.location.href = data.url;
  } catch (err) {
    console.error('Stripe checkout error:', err);
    alert('Could not open checkout. Please try again.');
  }
}

/**
 * Redirect a new (unauthenticated) user to registration checkout.
 * Used by the marketing site or public pricing page.
 * @param {{ planName: string, billingPeriod: string, email?: string, quantity?: number }} opts
 */
export async function redirectToRegistrationCheckout({ planName, billingPeriod, email, quantity }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  try {
    const res = await fetch(`${BASE}/api/billing/create-registration-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planName, billingPeriod, email, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || 'Failed to create checkout session');
    }
    const data = await res.json();
    window.location.href = data.url;
  } catch (err) {
    console.error('Stripe registration checkout error:', err);
    alert('Could not open checkout. Please try again.');
  }
}
